import React, { Component } from "react";
import { connect } from "react-redux";
import "./index.css";

import Overlay from "../Overlay";

// Prompt the candidate to refresh their CV when either:
//  - their previous session was more than this many months ago, or
//  - the CV currently on file was uploaded more than this many months ago.
const LOGIN_GAP_MONTHS = 6;
const CV_AGE_MONTHS = 12;

// How long "Not now" / "Update my CV" suppresses the prompt for, in days.
const SNOOZE_DAYS = 30;

const monthsSince = (date) => {
    if (!date) return null;
    const then = new Date(date).getTime();
    if (isNaN(then)) return null;
    return (Date.now() - then) / (1000 * 60 * 60 * 24 * 30.44);
};

class CVRefreshModal extends Component {

    state = { show: false, reason: null };

    componentDidMount() {

        const profile = this.props.user && this.props.user.data && this.props.user.data.profile;
        if (!profile) return;

        // Snooze is keyed per profile so a shared browser doesn't carry one
        // candidate's dismissal over to another.
        this.snoozeKey = `j4crew_cv_refresh_snooze_${profile._id}`;

        if (Date.now() < this.readSnooze()) return;

        // profile.lastActive is the PREVIOUS session timestamp - the server
        // returns the user doc before bumping lastActive to now - so this is a
        // genuine login gap, not "0 minutes ago".
        const loginGap = monthsSince(profile.lastActive);
        const cvAge = profile.cv && profile.cv.uploadDate ? monthsSince(profile.cv.uploadDate) : null;

        const awayAwhile = loginGap !== null && loginGap >= LOGIN_GAP_MONTHS;
        const staleCv = cvAge !== null && cvAge >= CV_AGE_MONTHS;

        if (!awayAwhile && !staleCv) return;

        // Snooze for a day as soon as it shows, so a reload on the same day
        // doesn't immediately re-trigger the prompt.
        this.writeSnooze(1);
        this.setState({ show: true, reason: staleCv ? "staleCv" : "awayAwhile" });

    }

    readSnooze = () => {
        try {
            const value = window.localStorage.getItem(this.snoozeKey);
            return value ? parseInt(value, 10) : 0;
        } catch (e) {
            return 0;
        }
    };

    writeSnooze = (days) => {
        try {
            window.localStorage.setItem(this.snoozeKey, String(Date.now() + days * 86400000));
        } catch (e) {
            /* localStorage unavailable (e.g. private mode) - prompt simply shows again next load */
        }
    };

    handleDismiss = () => {
        this.writeSnooze(SNOOZE_DAYS);
        this.setState({ show: false });
    };

    handleUpdate = () => {
        this.writeSnooze(SNOOZE_DAYS);
        this.setState({ show: false });
        this.props.history.push("/profile/documents");
    };

    render() {

        if (!this.state.show) return null;

        const body = this.state.reason === "staleCv"
            ? "The CV on your profile was uploaded over a year ago. Upload your latest one so captains and crew agents always see your most recent experience and qualifications."
            : "It has been a while since you were last here. Upload your latest CV so your profile reflects your most recent experience and qualifications.";

        return (
            <Overlay>
                <div className="cv-refresh-positioner">
                    <div className="cv-refresh-modal" role="dialog" aria-modal="true" aria-labelledby="cv-refresh-title">
                        <div className="cv-refresh-icon" aria-hidden="true">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6A2 2 0 0 0 4 4V20A2 2 0 0 0 6 22H18A2 2 0 0 0 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                <path d="M12 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <path d="M9 15L12 12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h2 className="cv-refresh-title" id="cv-refresh-title">Time to refresh your CV?</h2>
                        <p className="cv-refresh-body">{body}</p>
                        <div className="cv-refresh-actions">
                            <button type="button" className="cv-refresh-button cv-refresh-button--secondary" onClick={this.handleDismiss}>Not now</button>
                            <button type="button" className="cv-refresh-button cv-refresh-button--primary" onClick={this.handleUpdate}>Update my CV</button>
                        </div>
                    </div>
                </div>
            </Overlay>
        );

    }

}

const mapStateToProps = (state) => {
    return {
        user: state.user,
    };
};

export default connect(mapStateToProps)(CVRefreshModal);
